import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cn } from "../../lib/utils"

const Select = React.forwardRef(({ children, ...props }, ref) => (
  <SelectPrimitive.Root {...props} ref={ref}>
    {children}
  </SelectPrimitive.Root>
))

const SelectTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
      className
    )}
    {...props}
  />
))

/**
 * ⚠️ LES OPTIONS SONT DANS UN `Viewport`, ET C'EST LUI QUI FAIT DÉFILER.
 *
 * Elles étaient enfants directs de `Content`, qui porte `max-h-96` et
 * `overflow-hidden` : au-delà de 384 points — douze options de 32 points — les
 * suivantes étaient ROGNÉES, sans ascenseur et sans molette, puisqu'un élément
 * en `overflow: hidden` ne défile pas à la souris. Le menu n'avait pas l'air
 * cassé, il avait l'air COURT : personne ne cherche ce qui ne se voit pas.
 * Relevé le 04/10/2026 sur le filtre par classe de la campagne photos, qui
 * porte 27 entrées — quinze étaient hors d'atteinte. Les écoles ont 54 classes.
 *
 * Radix attend cette structure et la documente dans son propre code : il pose
 * `overflow: "hidden auto"` sur le Viewport, et donne au Content
 * `display: flex; flexDirection: column` — « flex layout so we can place the
 * scroll buttons properly ». Sans Viewport, il n'y a donc rien qui défile.
 *
 * ⚠️ NE PAS « SIMPLIFIER » EN REMETTANT LES OPTIONS SOUS `Content` : le menu
 * paraîtra correct sur les listes courtes, qui sont la majorité, et retombera
 * silencieusement sur les longues.
 *
 * Pas de `p-1` sur le Viewport, contrairement au modèle d'origine de ce
 * composant : cette liste déroulante est employée par 33 écrans vivants, et ce
 * correctif ne doit RIEN changer d'autre que le défilement.
 */
const SelectContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      position="popper"
      {...props}
    >
      <SelectPrimitive.Viewport>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-gray-100",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        {/* Optionnel: Ajouter une icône de sélection ici */}
      </SelectPrimitive.ItemIndicator>
    </span>
    {/* `flex-1` : sans lui, cette boite se reduisait a son contenu, et un
        libelle en deux parts - un nom a gauche, un compte a droite - ne
        pouvait pas tenir de colonne. Rien ne change pour un texte simple,
        qui reste aligne a gauche. */}
    <span className="min-w-0 flex-1 pl-6">
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </span>
  </SelectPrimitive.Item>
))

const SelectValue = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Value
    ref={ref}
    className={cn("flex h-9 w-full items-center gap-2", className)}
    {...props}
  />
))

const SelectGroup = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Group
    ref={ref}
    className={cn("p-1.5", className)}
    {...props}
  />
))
SelectGroup.displayName = SelectPrimitive.Group.displayName

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold text-muted-foreground", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName


const SelectPortal = SelectPrimitive.Portal

export { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectPortal 
}
